
import React from "react";
import { Task } from "../../types/tasks";
import { useAuth } from "../../contexts/AuthContext";
import TaskComments from "./TaskComments";
import TaskChangeRequests from "./TaskChangeRequests";
import TaskEditForm from "./detail/TaskEditForm";
import TaskViewOnly from "./detail/TaskViewOnly";
import ChangeRequestSection from "./detail/ChangeRequestSection";

interface TaskDetailViewProps {
  task: Task;
  isAdmin: boolean;
  isAdminView: boolean;
  currentUserId?: string;
  currentUserName?: string;
  currentUserAvatar?: string;
  onUpdateTask: (task: Task) => void;
  onAddComment: (taskId: string, content: string) => void;
  onRequestChange?: (taskId: string, requestType: "reassign" | "extend", details: string) => void;
  onChangeRequestResponse?: (taskId: string, requestId: string, approve: boolean) => void;
  onClose: () => void;
}

export const TaskDetailView = ({ 
  task, 
  isAdmin, 
  isAdminView,
  currentUserId,
  currentUserName,
  currentUserAvatar,
  onUpdateTask, 
  onAddComment,
  onRequestChange,
  onChangeRequestResponse,
  onClose 
}: TaskDetailViewProps) => {
  const { user } = useAuth();
  const userRole = user?.role;
  
  // Determine if this user can edit the task
  const isEditable = isAdmin && isAdminView;
  // Parents can now view and add comments to tasks
  const isParent = userRole === "parent";
  const isOwnTask = task.assignee && task.assignee.id === currentUserId;
  const canAddComments = isAdmin || isOwnTask || isParent;
  const canMarkComplete = isOwnTask || (isAdmin && isAdminView);
  
  // Count pending change requests for notification badge
  const pendingRequestsCount = task.changeRequests?.filter(req => req.status === "pending").length || 0;

  const handleCommentAdd = (content: string) => {
    onAddComment(task.id, content);
  };

  const handleRequestChangeWrapper = (requestType: "reassign" | "extend", details: string) => {
    if (onRequestChange) {
      onRequestChange(task.id, requestType, details);
    }
  };

  return (
    <div className="space-y-4">
      {isEditable ? (
        // Editable form for admins in All Tasks view
        <TaskEditForm 
          task={task} 
          onUpdateTask={onUpdateTask} 
          onClose={onClose}
          pendingRequestsCount={pendingRequestsCount}
        />
      ) : (
        // View-only display for non-admins and for admins viewing their own tasks
        <>
          <TaskViewOnly 
            task={task}
            canMarkComplete={canMarkComplete}
            onUpdateTask={onUpdateTask}
            onClose={onClose}
          />
          
          {/* Request changes section (for parents and assignees) */}
          {(isParent || isOwnTask) && onRequestChange && (
            <ChangeRequestSection 
              onRequestChange={handleRequestChangeWrapper}
            />
          )}
          
          {/* Show change requests if they exist */}
          {task.changeRequests && task.changeRequests.length > 0 && (
            <TaskChangeRequests
              requests={task.changeRequests}
              onRespond={isAdmin && isAdminView ? 
                (requestId: string, requestType: string, approve: boolean) => 
                  onChangeRequestResponse?.(task.id, requestId, approve) : undefined
              }
            />
          )}
        </>
      )}
      
      {canAddComments && (
        <TaskComments 
          comments={task.comments}
          currentUserId={currentUserId}
          currentUserName={currentUserName}
          currentUserAvatar={currentUserAvatar}
          onAddComment={handleCommentAdd}
        />
      )}
    </div>
  );
};
